import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { useCreateWorkPackage } from '../../hooks/useCapexQueries'
import { RemediatesSelect } from './RemediatesSelect'

interface CreateWorkPackageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  worksiteUid: string
  org: string
  buildingAssetUid?: string | null
  onCreated?: () => void
}

export function CreateWorkPackageDialog({
  open,
  onOpenChange,
  worksiteUid,
  org,
  buildingAssetUid,
  onCreated,
}: CreateWorkPackageDialogProps) {
  const { t } = useTranslation('capex')

  const [label, setLabel] = useState('')
  const [costLow, setCostLow] = useState('')
  const [costHigh, setCostHigh] = useState('')
  const [remediates, setRemediates] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createMutation = useCreateWorkPackage(org)

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!label.trim()) {
      newErrors.label = t('create_package_dialog.error_label_required')
    }

    const low = Number(costLow)
    const high = Number(costHigh)

    if (costLow === '' || isNaN(low) || low < 0) {
      newErrors.cost_low = t('create_package_dialog.error_cost_low_invalid')
    }

    if (costHigh === '' || isNaN(high) || high < 0) {
      newErrors.cost_high = t('create_package_dialog.error_cost_high_invalid')
    } else if (!newErrors.cost_low && high < low) {
      newErrors.cost_high = t('create_package_dialog.error_cost_high_below_low')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    try {
      await createMutation.mutateAsync({
        worksiteUid,
        body: {
          label: label.trim(),
          cost_low: Number(costLow),
          cost_high: Number(costHigh),
          ...(remediates ? { compliance_evaluation_uid: remediates } : {}),
        },
      })
      onCreated?.()
      resetAndClose()
    } catch {
      // error handled by mutation state
    }
  }

  const resetAndClose = () => {
    setLabel('')
    setCostLow('')
    setCostHigh('')
    setRemediates('')
    setErrors({})
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetAndClose() }}>
      <DialogContent data-testid="create-package-dialog">
        <DialogHeader>
          <DialogTitle>{t('create_package_dialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Label */}
          <div className="space-y-1.5">
            <Label htmlFor="pkg-label">{t('create_package_dialog.field_label')}</Label>
            <Input
              id="pkg-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('create_package_dialog.field_label_placeholder')}
              data-testid="create-package-label"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
            {errors.label && <p className="text-sm text-destructive">{errors.label}</p>}
          </div>

          {/* Cost range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pkg-cost-low">{t('create_package_dialog.field_cost_low')}</Label>
              <Input
                id="pkg-cost-low"
                type="number"
                min={0}
                value={costLow}
                onChange={(e) => setCostLow(e.target.value)}
                placeholder="0"
                data-testid="create-package-cost-low"
              />
              {errors.cost_low && <p className="text-sm text-destructive">{errors.cost_low}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pkg-cost-high">{t('create_package_dialog.field_cost_high')}</Label>
              <Input
                id="pkg-cost-high"
                type="number"
                min={0}
                value={costHigh}
                onChange={(e) => setCostHigh(e.target.value)}
                placeholder="0"
                data-testid="create-package-cost-high"
              />
              {errors.cost_high && <p className="text-sm text-destructive">{errors.cost_high}</p>}
            </div>
          </div>

          <RemediatesSelect
            org={org}
            buildingAssetUid={buildingAssetUid}
            value={remediates}
            onChange={setRemediates}
            id="pkg-remediates"
            testId="package-remediates-select"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose}>
            {t('create_package_dialog.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            data-testid="create-package-submit"
          >
            {createMutation.isPending
              ? t('create_package_dialog.submitting')
              : t('create_package_dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
